import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Alert, App, Button, Card, Col, Form, InputNumber, Popconfirm, Row, Skeleton, Space, Switch, Tag, Typography } from 'antd';
import { GlobalOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  DEFAULT_WARMUP_SETTINGS,
  SIGNAL_SUGGESTIONS,
  WARMUP_PRESETS,
  WARMUP_SUGGESTIONS,
  warmupDaysToTarget,
  warmupLimitForDay,
  type DomainWarmupItem,
  type WarmupSettings,
} from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { CardTitle } from '../../components/StatCard';
import { EmptyState, LoadError } from '../../components/states';
import { errorMessage } from '../../api/client';
import { useSaveWarmupSettings, useUpdateDomainWarmup, useWarmupDomains, useWarmupSettings } from '../../features/warmup/api';
import { fmtDate, plural } from '../../utils/format';
import { palette } from '../../app/theme';

export const PHASE_TAG = { off: { color: 'default', label: 'Warm-up off' }, warming: { color: 'processing', label: 'Warming up' }, complete: { color: 'success', label: 'Warm-up done' } } as const;

/** Settings → Email warm-up: each domain's ramp and daily limit, and the bounce/complaint limits that pause sending. */
export function WarmupSettingsPage() {
  const domains = useWarmupDomains();
  return (
    <div className="page page-narrow">
      <PageHeader
        title="Email warm-up"
        subtitle={
          <span>
            How many emails each domain may send a day, how fast that grows while a new domain warms up, and when sending pauses on its own. Progress and warm-up partners are on{' '}
            <Link to="/tools/warmup">Tools → Email warm-up</Link>, DNS checks on <Link to="/tools/domain-health">Tools → Domain health</Link>.
          </span>
        }
      />
      {domains.isError ? (
        <Card>
          <LoadError error={domains.error} onRetry={domains.refetch} title="Could not load domains" />
        </Card>
      ) : !domains.data ? (
        <Card>
          <Skeleton active />
        </Card>
      ) : !domains.data.length ? (
        <Card>
          <EmptyState description="Add a sending domain first." action={<Link to="/settings/domains"><Button type="primary">Email domains</Button></Link>} />
        </Card>
      ) : (
        domains.data.map((d) => <DomainWarmupCard key={d.id} domain={d} />)
      )}
      <SignalSettingsCard />
    </div>
  );
}

type DomainForm = {
  warmupEnabled: boolean;
  warmupStartVolume: number;
  warmupIncreasePercent: number;
  warmupTargetVolume: number;
  dailyLimit: number | null;
  warmupPartnerDaily: number;
};

function DomainWarmupCard({ domain: d }: { domain: DomainWarmupItem }) {
  const { message } = App.useApp();
  const update = useUpdateDomainWarmup();
  const [form] = Form.useForm<DomainForm>();
  const values = Form.useWatch([], form) as DomainForm | undefined;
  const enabled = values?.warmupEnabled ?? d.warmupEnabled;

  const save = async (v: DomainForm) => {
    try {
      const r = await update.mutateAsync({ id: d.id, ...v, dailyLimit: v.dailyLimit ?? null });
      message.success(r.warmupEnabled && !d.warmupEnabled ? `Warm-up started for ${d.domain}: ${r.todayLimit} emails today` : 'Saved');
    } catch (e) {
      message.error(errorMessage(e));
    }
  };
  const restart = () =>
    update
      .mutateAsync({ id: d.id, restart: true })
      .then((r) => message.success(`Warm-up restarted: day 1, ${r.todayLimit} emails today`))
      .catch((e) => message.error(errorMessage(e)));

  const status =
    d.phase === 'warming'
      ? `Day ${(d.day ?? 0) + 1} of about ${d.daysToTarget + 1}, started ${fmtDate(d.warmupStartedAt)}. Today's limit: ${d.todayLimit?.toLocaleString()}.`
      : d.phase === 'complete'
        ? `Reached ${d.warmupTargetVolume.toLocaleString()} a day. ${d.dailyLimit ? `The daily limit of ${d.dailyLimit.toLocaleString()} applies now.` : 'No daily limit now.'}`
        : d.dailyLimit
          ? `No warm-up. Daily limit: ${d.dailyLimit.toLocaleString()}.`
          : 'No warm-up and no daily limit: campaigns send as fast as Resend allows.';

  return (
    <Card
      style={{ marginBottom: 24 }}
      title={
        <CardTitle
          title={
            <Space size={8} wrap>
              <GlobalOutlined style={{ color: palette.textSecondary }} />
              {d.domain}
              <Tag color={PHASE_TAG[d.phase].color}>{PHASE_TAG[d.phase].label}</Tag>
              {d.sendingPausedAt && <Tag color="error">Sending paused</Tag>}
            </Space>
          }
          subtitle={status}
        />
      }
    >
      {!d.verified && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title="Not verified in Resend yet" description="Nothing sends from this domain until Resend verifies it. The warm-up can be set up already." />}
      <Form<DomainForm>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ warmupEnabled: d.warmupEnabled, warmupStartVolume: d.warmupStartVolume, warmupIncreasePercent: d.warmupIncreasePercent, warmupTargetVolume: d.warmupTargetVolume, dailyLimit: d.dailyLimit, warmupPartnerDaily: d.warmupPartnerDaily }}
        onFinish={save}
      >
        <Form.Item name="warmupEnabled" label="Warm up this domain" valuePropName="checked" extra="Campaigns from this domain stop at each day's limit and carry on the next day. One-to-one emails always go out, but count toward the limit.">
          <Switch />
        </Form.Item>
        {enabled && (
          <>
            <Form.Item label="Start from a suggestion">
              <Space wrap>
                {WARMUP_PRESETS.map((p) => (
                  <Button key={p.key} size="small" title={p.description} onClick={() => form.setFieldsValue({ warmupStartVolume: p.startVolume, warmupIncreasePercent: p.increasePercent })}>
                    {p.label}: {p.startVolume}, +{p.increasePercent}%/day
                  </Button>
                ))}
              </Space>
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name="warmupStartVolume" label="Emails on day 1" rules={[{ required: true }]} extra={WARMUP_SUGGESTIONS.startVolume}>
                  <InputNumber min={1} max={100000} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="warmupIncreasePercent" label="Daily increase" rules={[{ required: true }]} extra={WARMUP_SUGGESTIONS.increasePercent}>
                  <InputNumber min={1} max={100} suffix="%" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="warmupTargetVolume" label="Target a day" rules={[{ required: true }]} extra={WARMUP_SUGGESTIONS.targetVolume}>
                  <InputNumber min={1} max={1000000} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <SchedulePreview start={values?.warmupStartVolume} increase={values?.warmupIncreasePercent} target={values?.warmupTargetVolume} today={d.phase === 'warming' ? d.day : null} />
          </>
        )}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="dailyLimit" label={enabled ? 'Daily limit after warm-up' : 'Daily limit'} extra={WARMUP_SUGGESTIONS.dailyLimit}>
              <InputNumber min={1} max={1000000} placeholder="No limit" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="warmupPartnerDaily" label="Warm-up emails per agent each weekday" extra={<span>{WARMUP_SUGGESTIONS.partnerDaily} Sent while warm-up is on, to the partners on <Link to="/tools/warmup">Tools → Email warm-up</Link>.</span>}>
              <InputNumber min={0} max={20} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Space wrap>
          <Button type="primary" htmlType="submit" loading={update.isPending && !update.variables?.restart}>Save</Button>
          {d.warmupEnabled && d.warmupStartedAt && (
            <Popconfirm title="Start the warm-up over from day 1?" description="Use this after a long break from sending, or after the domain was paused." okText="Restart" onConfirm={restart}>
              <Button icon={<ReloadOutlined />} loading={update.isPending && !!update.variables?.restart}>Restart warm-up</Button>
            </Popconfirm>
          )}
        </Space>
      </Form>
    </Card>
  );
}

const PREVIEW_DAYS = [0, 1, 2, 4, 6, 9, 13, 20, 27];

function SchedulePreview({ start, increase, target, today }: { start?: number; increase?: number; target?: number; today: number | null }) {
  if (!start || !increase || !target || target < start) return null;
  const s = { startVolume: start, increasePercent: increase, targetVolume: target };
  const reach = warmupDaysToTarget(s);
  const days = PREVIEW_DAYS.filter((d) => d < reach);
  const weeks = Math.round(((reach + 1) / 7) * 10) / 10;
  return (
    <div style={{ background: palette.canvas, border: `1px solid ${palette.border}`, borderRadius: 8, padding: '12px 16px', marginBottom: 24 }}>
      <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
        Reaches {target.toLocaleString()} a day on day {reach + 1} ({reach + 1 < 14 ? plural(reach + 1, 'day') : `about ${weeks} weeks`})
      </Typography.Text>
      <Space size={[6, 6]} wrap>
        {days.map((d) => (
          <Tag key={d} color={today === d ? 'processing' : undefined} style={{ marginInlineEnd: 0 }}>
            Day {d + 1}: {warmupLimitForDay(s, d).toLocaleString()}
          </Tag>
        ))}
        <Tag color="success" style={{ marginInlineEnd: 0 }}>
          Day {reach + 1}: {target.toLocaleString()}
        </Tag>
      </Space>
      {reach + 1 < 10 && <Typography.Text type="warning" style={{ display: 'block', marginTop: 8, fontSize: 13 }}>Under 10 days is fast for a new domain. Lower the daily increase if the domain has little sending history.</Typography.Text>}
    </div>
  );
}

function SignalSettingsCard() {
  const { message } = App.useApp();
  const settings = useWarmupSettings();
  const save = useSaveWarmupSettings();
  const [form] = Form.useForm<WarmupSettings>();
  const autoPause = Form.useWatch('autoPause', form);
  useEffect(() => {
    if (settings.data) form.setFieldsValue(settings.data);
  }, [settings.data, form]);

  return (
    <Card title={<CardTitle title="Pause on bad signals" subtitle="Applies to every domain. A paused domain's campaigns wait where they are and its warm-up emails stop until you resume it." />}>
      {settings.isError ? (
        <LoadError error={settings.error} onRetry={settings.refetch} title="Could not load the settings" />
      ) : !settings.data ? (
        <Skeleton active />
      ) : (
        <Form<WarmupSettings>
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={settings.data}
          onFinish={(v) => save.mutateAsync(v).then(() => message.success('Saved')).catch((e) => message.error(errorMessage(e)))}
        >
          <Form.Item name="autoPause" label="Pause sending automatically" valuePropName="checked" extra="Rates are checked before every campaign batch and every 10 minutes. Everyone gets an email when a domain is paused (Settings → Notifications).">
            <Switch />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="maxBounceRate" label="Pause at bounce rate" rules={[{ required: true }]} extra={SIGNAL_SUGGESTIONS.maxBounceRate}>
                <InputNumber min={0.5} max={20} step={0.5} suffix="%" disabled={!autoPause} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="maxComplaintRate" label="Pause at spam complaint rate" rules={[{ required: true }]} extra={SIGNAL_SUGGESTIONS.maxComplaintRate}>
                <InputNumber min={0.01} max={5} step={0.05} suffix="%" disabled={!autoPause} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="minSent" label="Judge rates from" rules={[{ required: true }]} extra={SIGNAL_SUGGESTIONS.minSent}>
                <InputNumber min={10} max={100000} suffix="emails" disabled={!autoPause} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="windowDays" label="Over the last" rules={[{ required: true }]} extra={SIGNAL_SUGGESTIONS.windowDays}>
                <InputNumber min={1} max={30} suffix="days" disabled={!autoPause} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending}>Save</Button>
            <Button onClick={() => form.setFieldsValue(DEFAULT_WARMUP_SETTINGS)}>Use suggested values</Button>
          </Space>
        </Form>
      )}
    </Card>
  );
}
